import React, { useEffect, useState } from "react";
import { Sun, Share2, Copy, Check } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import { SITE } from "../i18n/translations";
import DarshanSubscribe from "./DarshanSubscribe";

export default function DailyDarshan() {
  const { t, lang } = useLang();
  const [item, setItem] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/darshan/today")
      .then((r) => alive && setItem(r.data || null))
      .catch(() => alive && setItem(null));
    return () => { alive = false; };
  }, []);

  if (!item) return null;

  const text = (lang === "bn" && item.text_bn) || (lang === "hi" && item.text_hi) || item.text_en;
  const shareText = `🪔 ${t("darshan_title")} — ${SITE.name}\n\n“${text}”\n\n${SITE.domain}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      toast.success(t("darshan_copied"));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t("darshan_copy_failed"));
    }
  };

  const today = new Date(`${item.date}T00:00:00`).toLocaleDateString(undefined, {
    weekday: "long", day: "numeric", month: "long",
  });

  return (
    <motion.section
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-20"
      data-testid="daily-darshan"
    >
      <div className="glass-liquid relative overflow-hidden rounded-[2rem] p-8 sm:p-12">
        <span className="orb orb-gold h-64 w-64 -top-20 right-1/4" />
        <div className="pattern-sacred absolute inset-0 opacity-20" />

        <div className="relative max-w-3xl">
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <span className="badge-gold inline-flex items-center gap-2 rounded-full px-3.5 py-1 text-[10px] font-serif uppercase tracking-[0.24em]">
              <Sun className="h-3 w-3 flame-flicker" /> {t("darshan_title")}
            </span>
            <span className="text-[11px] uppercase tracking-[0.16em] text-tara-muted">{today}</span>
          </div>

          <blockquote
            className="font-display text-2xl sm:text-3xl lg:text-[2.1rem] leading-[1.45] text-tara-ink italic mb-8"
            data-testid="darshan-text"
          >
            “{text}”
          </blockquote>

          <div className="flex flex-wrap items-center gap-3">
            <a
              href={`https://wa.me/?text=${encodeURIComponent(shareText)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 rounded-full bg-green-600 px-6 py-3.5 text-sm font-serif font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-green-700"
              data-testid="darshan-share-btn"
            >
              <Share2 className="h-4 w-4" /> {t("darshan_share")}
            </a>
            <button
              onClick={copy}
              className="btn-ghost-gold flex items-center gap-2 rounded-full px-6 py-3.5 text-sm font-serif font-semibold"
              data-testid="darshan-copy-btn"
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} {t("darshan_copy")}
            </button>
            <DarshanSubscribe />
            <span className="text-xs text-tara-muted">{t("darshan_note")}</span>
          </div>
        </div>
      </div>
    </motion.section>
  );
}
